// ArchaeoHub - Main Application Entry Point
import './styles/index.css';
import router from './router.js';
import { onAuthChange, getCurrentUser } from './firebase/auth.js';
import { getSites, getSite, getLogEntries, getAssets, getFolders } from './firebase/db.js';
import { createNavigation, initNavigation } from './components/Navigation.js';
import { createLoginView, initLoginView } from './views/LoginView.js';
import { createDashboardView, initDashboardView } from './views/DashboardView.js';
import { createSiteDetailView, initSiteDetailView } from './views/SiteDetailView.js';
import { createLogbookView, initLogbookView } from './views/LogbookView.js';
import { createAssetsView, initAssetsView } from './views/AssetsView.js';
import { createReconstructView, initReconstructView, cleanupReconstructView } from './views/ReconstructView.js';
import { createLandingView, initLandingView } from './views/LandingView.js';
import { createDeleteAccountView, initDeleteAccountView } from './views/DeleteAccountView.js';
import { createVerificationView, initVerificationView } from './views/VerificationView.js';

// Current user state
let currentUser = null;

// App container
const app = document.getElementById('app');

/**
 * Render the application
 * @param {string} content - Main content HTML
 * @param {boolean} showNav - Whether to show navigation
 */
function render(content, showNav = true) {
    app.innerHTML = `
    ${showNav ? createNavigation(currentUser) : ''}
    <main class="fade-in">
      ${content}
    </main>
  `;

    if (showNav) {
        initNavigation();
    }
}

/**
 * Show loading state
 */
function showLoading() {
    app.innerHTML = `
    <div class="app-loader">
      <div class="loader-content">
        <div class="loader-icon">🏛️</div>
        <div class="loader-spinner"></div>
        <p>Loading...</p>
      </div>
    </div>
  `;
}

// ============ ROUTE HANDLERS ============

// Landing page (Root)
router.on('/', async () => {
    render(createLandingView(currentUser), false); // No nav for landing
    initLandingView();
});

// Login page
router.on('/login', async () => {
    if (currentUser) {
        router.navigate('/dashboard');
        return;
    }
    render(createLoginView(), false);
    initLoginView();
});

// Dashboard
router.on('/dashboard', async () => {
    if (!currentUser) {
        router.navigate('/login');
        return;
    }
    showLoading();
    try {
        const sites = await getSites(currentUser.uid);
        render(createDashboardView(sites));
        initDashboardView();
    } catch (error) {
        console.error('Error loading sites:', error);
        render(`
      <div class="page">
        <div class="container">
          <div class="empty-state">
            <div class="empty-state-icon">⚠️</div>
            <h2 class="empty-state-title">Error Loading Sites</h2>
            <p class="empty-state-text">Please check your Firebase configuration and try again.</p>
            <button class="btn btn-primary" onclick="location.reload()">Retry</button>
          </div>
        </div>
      </div>
    `);
    }
});

// Site detail
router.on('/site/:id', async (params) => {
    if (!currentUser) {
        router.navigate('/login');
        return;
    }

    showLoading();

    try {
        const site = await getSite(params.id);
        render(createSiteDetailView(site));
        initSiteDetailView(site);
    } catch (error) {
        console.error('Site detail error:', error);
        render(createSiteDetailView(null));
    }
});

// Logbook
router.on('/site/:id/logbook', async (params) => {
    if (!currentUser) {
        router.navigate('/login');
        return;
    }

    showLoading();

    try {
        const [site, entries] = await Promise.all([
            getSite(params.id),
            getLogEntries(params.id)
        ]);
        render(createLogbookView(site, entries));
        initLogbookView(site, entries);
    } catch (error) {
        console.error('Logbook error:', error);
        render(createLogbookView(null, []));
    }
});

// 3D Assets
router.on('/site/:id/assets', async (params) => {
    if (!currentUser) {
        router.navigate('/login');
        return;
    }

    showLoading();

    try {
        const [site, assets, folders] = await Promise.all([
            getSite(params.id),
            getAssets(params.id),
            getFolders(params.id)
        ]);

        // Parse query params for current folder
        // Router params only give us :id, so we need to parse window.location.hash manually for query params
        // hash format: #/site/:id/assets?folder=xyz
        const hash = window.location.hash;
        const queryPart = hash.split('?')[1];
        const urlParams = new URLSearchParams(queryPart);
        const folderId = urlParams.get('folder') || null;

        render(createAssetsView(site, assets, folders, folderId));
        initAssetsView(site, assets, folders, currentUser);
    } catch (error) {
        console.error('Assets error:', error);
        render(createAssetsView(null, [], [], null));
    }
});

// Reconstruct
router.on('/site/:id/reconstruct', async (params) => {
    if (!currentUser) {
        router.navigate('/login');
        return;
    }

    // Cleanup previous viewer if exists
    cleanupReconstructView();

    showLoading();

    try {
        const [site, assets] = await Promise.all([
            getSite(params.id),
            getAssets(params.id)
        ]);
        render(createReconstructView(site, assets));
        initReconstructView(site, assets);
    } catch (error) {
        console.error('Reconstruct error:', error);
        render(createReconstructView(null, []));
    }
});

// Delete account
router.on('/delete-account', async () => {
    if (!currentUser) {
        router.navigate('/login');
        return;
    }
    render(createDeleteAccountView(), true);
    initDeleteAccountView();
});

// Ownership Verification
router.on('/verification', async () => {
    render(createVerificationView(), false);
    initVerificationView();
});

// 404 - Not found
router.on('*', () => {
    render(`
    <div class="page">
      <div class="container">
        <div class="empty-state">
          <div class="empty-state-icon">🔍</div>
          <h2 class="empty-state-title">Page Not Found</h2>
          <p class="empty-state-text">The page you're looking for doesn't exist.</p>
          <a href="#/dashboard" class="btn btn-primary">
            ${currentUser ? 'Go to Dashboard' : 'Back to Login'}
          </a>
        </div>
      </div>
    </div>
  `);
});

// ============ INITIALIZATION ============

// Authentication state changes
onAuthChange((user) => {
    currentUser = user;
    const path = router.getPath();

    // If on landing or login, and just logged in, go to dashboard
    if (user && (path === '/' || path === '/login')) {
        router.navigate('/dashboard');
    } else if (!user && path !== '/' && path !== '/login' && path !== '/delete-account') {
        // If logged out and not on public pages or delete-account, go to landing
        router.navigate('/');
    } else {
        // Refresh current route to update nav/content
        router.handleRoute();
    }
});

// Log app initialization
console.log('🏛️ ArchaeoHub initialized');
console.log('📖 Digital Excavation Platform v1.0.0');
